import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { articles } from "@/mocks/articles";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [articlesOpen, setArticlesOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const articlesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (articlesRef.current && !articlesRef.current.contains(e.target as Node)) {
        setArticlesOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const showDarkBg = !isHome || scrolled;
  const textClass = showDarkBg ? "text-foreground-950" : "text-white drop-shadow-sm";
  const bgClass = showDarkBg ? "bg-background-50/95 backdrop-blur-md" : "bg-gradient-to-b from-black/30 to-transparent";

  return (
    <nav className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${bgClass}`}>
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            className={`text-lg md:text-xl font-extrabold whitespace-nowrap cursor-pointer transition-colors duration-300 ${textClass}`}
          >
            CSSBuy<span className="font-light opacity-70">.run</span>
          </Link>

          <div className="hidden md:flex items-center gap-5">
            <Link
              to="/"
              className={`text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors duration-300 hover:opacity-80 ${textClass}`}
            >
              Início
            </Link>

            <Link
              to="/artigo/cssbuy-spreadsheet-guia-completo"
              className={`text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors duration-300 hover:opacity-80 ${textClass}`}
            >
              Guia de Compras
            </Link>

            <div className="relative" ref={articlesRef}>
              <button
                onClick={() => setArticlesOpen(!articlesOpen)}
                className={`text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors duration-300 hover:opacity-80 inline-flex items-center gap-1 ${textClass}`}
              >
                Artigos
                {articlesOpen ? <i className="ri-arrow-up-s-line text-xs"></i> : <i className="ri-arrow-down-s-line text-xs"></i>}
              </button>

              {articlesOpen && (
                <div className="absolute top-full mt-2 left-0 bg-white rounded-xl shadow-lg border border-background-200 py-2 min-w-[260px] z-50">
                  <Link
                    to="/artigo/cssbuy-spreadsheet-guia-completo"
                    onClick={() => setArticlesOpen(false)}
                    className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-50 hover:text-primary-500 transition-colors duration-150 cursor-pointer whitespace-nowrap"
                  >
                    Guia Completo CSSBuy
                  </Link>
                  <div className="border-t border-background-100 my-1"></div>
                  {articles.map((a) => (
                    <Link
                      key={a.slug}
                      to={`/artigo/${a.slug}`}
                      onClick={() => setArticlesOpen(false)}
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-50 hover:text-primary-500 transition-colors duration-150 cursor-pointer whitespace-nowrap"
                    >
                      {a.title.length > 42 ? a.title.slice(0, 42) + "..." : a.title}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="bg-primary-500 text-white text-sm font-semibold px-5 py-2.5 rounded-full whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-all duration-200 shadow-sm"
            >
              cssbuy sheet
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden w-9 h-9 flex items-center justify-center cursor-pointer ${textClass}`}
            aria-label="Menu"
          >
            <i className={`ri-${mobileOpen ? "close" : "menu"}-line text-xl`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 shadow-lg">
          <div className="px-4 py-4 flex flex-col gap-1 max-h-[70vh] overflow-y-auto">
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 text-foreground-900 font-semibold text-sm py-3 px-3 rounded-lg hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-home-4-line text-primary-500 w-5 h-5 flex items-center justify-center"></i>
              Início
            </Link>

            <div className="border-t border-background-100 my-1"></div>

            <p className="text-foreground-400 text-xs font-semibold uppercase tracking-wider px-3 py-1">
              Guias
            </p>
            <Link
              to="/artigo/cssbuy-spreadsheet-guia-completo"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 text-foreground-700 text-sm py-3 px-3 rounded-lg hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-book-open-line text-primary-500 w-5 h-5 flex items-center justify-center"></i>
              Guia de Compras Completo
            </Link>

            <div className="border-t border-background-100 my-1"></div>

            <p className="text-foreground-400 text-xs font-semibold uppercase tracking-wider px-3 py-1">
              Artigos SEO
            </p>
            {articles.map((a) => (
              <Link
                key={a.slug}
                to={`/artigo/${a.slug}`}
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 text-foreground-700 text-sm py-2.5 px-3 rounded-lg hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-article-line text-foreground-400 w-5 h-5 flex items-center justify-center"></i>
                {a.title.length > 38 ? a.title.slice(0, 38) + "..." : a.title}
              </Link>
            ))}

            <div className="border-t border-background-100 my-1"></div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMobileOpen(false)}
              className="flex items-center justify-center gap-2 bg-primary-500 text-white text-sm font-semibold py-3 px-5 rounded-full cursor-pointer mt-1"
            >
              <i className="ri-external-link-line"></i>
              cssbuy sheet
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}